/**
 * `TVPreviewModal` — preview "como TV" da playlist (TH-53378).
 *
 * Cycla pelos slides ativos respeitando `duration_ms` de cada um. Sem
 * fetch — opera 100% sobre o rascunho do editor, então o usuário pode
 * pre-visualizar mudanças *antes* de salvar (DoD: "Visualizar como TV").
 *
 * Não tenta replicar 100% do player Android (sem schedule de horário,
 * sem ETag etc). É uma simulação de ordem + duração + thumbnail —
 * suficiente pra confirmar "isso aqui faz sentido".
 *
 * Slides inativos são pulados na simulação (mesma regra do player).
 */

import { useEffect, useMemo, useState } from "react";
import { ImageIcon, Pause, Play } from "lucide-react";

import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/Dialog";
import { cn } from "@/lib/utils";
import type { CustomArtRead } from "@/hooks/useCreateCustomArt";
import type { DraftSlide } from "@/components/playlist/PlaylistSlideRow";

export interface TVPreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  slides: DraftSlide[];
  customArtsById: ReadonlyMap<string, CustomArtRead>;
  tvName?: string;
}

export function TVPreviewModal({
  open,
  onOpenChange,
  slides,
  customArtsById,
  tvName,
}: TVPreviewModalProps) {
  const active = useMemo(() => slides.filter((s) => s.active), [slides]);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);

  // Reset ao abrir; congela ao fechar (não consome timer fechado).
  useEffect(() => {
    if (open) {
      setIndex(0);
      setPlaying(true);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !playing || active.length === 0) return;
    const current = active[index % active.length];
    const ms = Math.max(1000, current.duration_ms);
    const t = window.setTimeout(() => {
      setIndex((i) => (i + 1) % active.length);
    }, ms);
    return () => window.clearTimeout(t);
  }, [open, playing, index, active]);

  const current = active.length > 0 ? active[index % active.length] : null;
  const thumbUrl = resolveThumb(current, customArtsById);
  const label = resolveLabel(current, customArtsById);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogTitle>
          Preview {tvName ? `— ${tvName}` : "da playlist"}
        </DialogTitle>
        <DialogDescription>
          Simulação local sem ETag nem schedule. Slides inativos são pulados.
        </DialogDescription>

        <div className="mt-4">
          {active.length === 0 ? (
            <div className="flex aspect-video items-center justify-center rounded-md border border-dashed border-[var(--color-border-strong)] bg-[var(--color-background)] text-sm text-[var(--color-muted-foreground)]">
              Nenhum slide ativo pra exibir.
            </div>
          ) : (
            <>
              <div className="relative flex aspect-video items-center justify-center overflow-hidden rounded-md border border-[var(--color-border)] bg-black">
                {thumbUrl ? (
                  <img
                    src={thumbUrl}
                    alt={label}
                    className="h-full w-full object-contain"
                    draggable={false}
                  />
                ) : (
                  <ImageIcon
                    className="h-10 w-10 text-[var(--color-muted-foreground)]"
                    aria-hidden="true"
                  />
                )}
                <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between gap-2 bg-black/60 px-3 py-2 text-xs text-white">
                  <span className="truncate">{label}</span>
                  <span className="font-mono tabular-nums">
                    {index + 1} / {active.length} ·{" "}
                    {Math.round((current?.duration_ms ?? 0) / 1000)}s
                  </span>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between gap-2">
                <div className="flex gap-1">
                  {active.map((s, i) => (
                    <span
                      key={s._localId}
                      className={cn(
                        "h-1.5 w-6 rounded-full",
                        i === index
                          ? "bg-[var(--color-primary)]"
                          : "bg-[var(--color-border-strong)]",
                      )}
                      aria-hidden="true"
                    />
                  ))}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPlaying((p) => !p)}
                >
                  {playing ? (
                    <>
                      <Pause className="h-4 w-4" aria-hidden="true" />
                      Pausar
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4" aria-hidden="true" />
                      Play
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function resolveThumb(
  slide: DraftSlide | null,
  customArtsById: ReadonlyMap<string, CustomArtRead>,
): string | null {
  if (slide === null) return null;
  if (slide.product_art?.art_url) return slide.product_art.art_url;
  if (slide.custom_art_id) {
    const ca = customArtsById.get(slide.custom_art_id);
    if (ca?.art_url && /^https?:\/\//i.test(ca.art_url)) return ca.art_url;
  }
  return null;
}

function resolveLabel(
  slide: DraftSlide | null,
  customArtsById: ReadonlyMap<string, CustomArtRead>,
): string {
  if (slide === null) return "—";
  if (slide.product_art?.product_name) return slide.product_art.product_name;
  if (slide.custom_art_id) {
    const ca = customArtsById.get(slide.custom_art_id);
    if (ca?.name) return ca.name;
  }
  return "Arte sem nome";
}
